import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import SafariExperiencesCarousel from '../../components/SafariExperiencesCarousel'
import FAQSection from '../../components/FAQSection'
import { safariExperiences } from '../../data/safariExperiences'

export default function GorillaChimp7Days() {
    const [isModalOpen, setIsModalOpen] = useState(false)

    const faqs = [
        {
            question: "Is 7 days enough for gorillas and chimpanzees?",
            answer: "Absolutely! Seven days is ideal for a focused primate safari. You'll have quality time for both gorilla and chimpanzee trekking without feeling rushed, plus game drives and boat safaris in Queen Elizabeth. The pacing is comfortable with reasonable driving distances between parks."
        },
        {
            question: "Should I start from Entebbe or Kigali?",
            answer: "Both work well. Starting from Kigali (Rwanda) actually reduces driving time to Bwindi significantly—it's only 4-5 hours versus 8-9 hours from Kampala. Many travelers combine international flights to Kigali with this itinerary. We can design the route to work optimally from either airport."
        },
        {
            question: "What's the success rate for gorilla and chimp sightings?",
            answer: "Extremely high—over 98% for both! Rangers track gorilla families daily before tourists arrive, so they know exactly where they are. Kibale's habituated chimpanzee communities are also consistently located. In our years of operation, sighting failures are exceptionally rare."
        },
        {
            question: "How physically demanding is this safari?",
            answer: "Moderate fitness is recommended. Gorilla trekking can be challenging (2-6 hours over steep terrain), but rangers assign you to families based on your fitness level. Chimpanzee tracking is generally easier (2-3 hours on better paths). The rest involves comfortable game drives and boat cruises. Hiring a porter for gorilla trekking helps significantly with the challenging sections."
        },
        {
            question: "When should I book my permits?",
            answer: "Book 3-6 months in advance for peak season (June-September, December-February). Gorilla permits are limited and sell out quickly. We'll secure your permits immediately upon receiving your confirmed travel dates. Wet season months offer more availability and often lower accommodation rates."
        }
    ]

    const itinerary = [
        {
            day: 1,
            title: "Arrival & Transfer to Kibale Forest",
            description: "Your adventure begins with arrival at Entebbe International Airport or Kigali International Airport (Rwanda). You will meet your safari guide and we'll embark on a scenic drive to Kibale Forest National Park, the 'Primate Capital of the World.' Together, we'll journey through stunning views of Uganda's countryside, tea plantations, and crater lakes near Fort Portal. You will arrive at your lodge for dinner and overnight.",
            highlights: ["Airport pickup", "Scenic countryside drive", "Optional Ndali Crater Lake viewpoint"]
        },
        {
            day: 2,
            title: "Chimpanzee Tracking & Bigodi Wetland Walk",
            description: "After an early breakfast, we'll head into Kibale Forest for chimpanzee tracking. With over 1,500 chimpanzees and 12 other primate species, Kibale offers exceptional primate viewing. You will be guided by expert rangers through dense vegetation to find habituated chimpanzee communities. In the afternoon, we'll explore Bigodi Wetland Sanctuary on a guided walk where you will discover diverse birdlife and primates in this community-run conservation area.",
            highlights: ["Chimpanzee tracking in Kibale", "Bigodi Wetland Sanctuary walk", "Primate and bird spotting"]
        },
        {
            day: 3,
            title: "Transfer to Queen Elizabeth & Afternoon Game Drive",
            description: "We'll travel to Queen Elizabeth National Park, Uganda's most biodiverse protected area. The park's varied ecosystems—savannah, wetlands, forests, and crater lakes—support incredible wildlife diversity. Upon arrival, you will check into your lodge before we embark on an afternoon game drive, searching for elephants, buffaloes, Uganda kobs, and other savannah species as they become more active in the cooler hours.",
            highlights: ["Transfer to Queen Elizabeth", "Afternoon game drive", "Crater lake scenery"]
        },
        {
            day: 4,
            title: "Morning Game Drive & Kazinga Channel Boat Safari",
            description: "We'll start with an early morning game drive along the Kasenyi plains, prime territory for spotting lions, leopards, elephants, and abundant antelopes. You will return to the lodge for lunch. In the afternoon, we'll enjoy a memorable boat cruise on the Kazinga Channel. This natural waterway connecting Lakes Edward and George is famous for having Africa's highest concentration of hippos, plus Nile crocodiles and over 600 bird species that you will observe.",
            highlights: ["Morning game drive on Kasenyi plains", "Kazinga Channel boat cruise", "Exceptional birdwatching"]
        },
        {
            day: 5,
            title: "Ishasha Tree-Climbing Lions & Transfer to Bwindi",
            description: "After breakfast, we'll drive through the Ishasha sector of Queen Elizabeth National Park, famous for its tree-climbing lions. You will watch for these majestic predators lounging in fig trees—a rare behavior found in only two places worldwide. We'll continue your journey to Bwindi Impenetrable National Park, home to nearly half the world's mountain gorillas. You will enjoy the scenic drive that showcases Uganda's stunning highlands and terraced hillsides.",
            highlights: ["Tree-climbing lions in Ishasha", "Scenic highland drive", "Arrive at Bwindi"]
        },
        {
            day: 6,
            title: "Gorilla Trekking in Bwindi & Cultural Experience",
            description: "Today is the highlight of your safari! After an early morning briefing at park headquarters, you will trek into Bwindi's ancient rainforest with experienced rangers to find a habituated gorilla family. The trek can last 2-6 hours depending on the gorillas' location. Once found, you will spend one magical hour observing these gentle giants as they feed, play, and interact—a truly life-changing experience. In the afternoon, if you wish, we can arrange an optional visit to the Batwa pygmy community where you will learn about their traditional forest lifestyle.",
            highlights: ["Gorilla trekking experience", "1 hour with mountain gorillas", "Optional Batwa cultural visit"]
        },
        {
            day: 7,
            title: "Return to Entebbe/Kampala",
            description: "After breakfast, we'll begin your scenic journey back to Entebbe or Kampala for your departure. The drive allows time for you to reflect on your incredible primate encounters and wildlife experiences. We'll stop at the Equator monument where you will have photo opportunities and time for souvenir shopping. You will arrive in Entebbe for your departure flight or overnight stay, depending on your schedule.",
            highlights: ["Scenic return drive", "Equator monument stop", "Departure"]
        }
    ]

    return (
        <div>
            {/* Hero Section */}
            <section
                className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
                style={{
                    backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(/images/packages/7-days-gorilla-chimp.jpg)',
                }}
            >
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl text-white">
                        <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
                            7 Days Gorilla & Chimpanzee Trekking Safari – 2025/2026
                        </h1>
                        <p className="text-xl md:text-2xl mb-6">
                            Meet our closest relatives in their forest homes
                        </p>
                        <div className="flex items-center gap-6 mb-6">
                            <div className="flex items-center gap-2">
                                <div className="flex text-safari-gold">
                                    {[...Array(5)].map((_, i) => (
                                        <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                                    ))}
                                </div>
                                <span className="text-lg">(410+ reviews)</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4 mb-8">
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                                UWA Licensed
                            </div>
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-heart" className="w-4 h-4 mr-2" />
                                Conservation Supported
                            </div>
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-leaf" className="w-4 h-4 mr-2" />
                                Eco-Certified
                            </div>
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                            Request Custom Itinerary
                        </button>
                    </div>
                </div>
            </section>

            {/* Page Content */}
            <div className="container mx-auto px-4 py-16">
                <div className="lg:grid lg:grid-cols-3 lg:gap-12">
                    {/* Main Content */}
                    <main className="lg:col-span-2">
                        {/* Safari Description */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                                The Ultimate Primate Safari Experience
                            </h2>

                            <div className="prose prose-lg max-w-none">
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    Seven days focused on what Uganda does best: primate encounters. This safari is designed for travelers who want deep, meaningful connections with our closest evolutionary relatives—gorillas and chimpanzees—while also experiencing classic East African wildlife viewing. It's the perfect duration for a focused primate experience without feeling rushed.
                                </p>

                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    Your journey takes you through three of Uganda's most spectacular national parks. In Kibale Forest, you'll track chimpanzees through dense canopy where they feed, play, and socialize in troops. Queen Elizabeth National Park adds classic safari experiences—game drives searching for lions and elephants, boat cruises past hippos and crocodiles, and the unique sight of tree-climbing lions in Ishasha. The grand finale? An hour with mountain gorillas in Bwindi's misty rainforest, watching silverbacks keep watch while youngsters tumble and play.
                                </p>

                                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Why This Safari Works</h3>
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    This 7-day itinerary strikes an ideal balance between primate tracking and wildlife viewing, with comfortable pacing that lets you absorb each experience. You're not spending excessive time driving—the route flows naturally from Kibale through Queen Elizabeth to Bwindi. Each park offers distinctly different ecosystems and wildlife, preventing the "safari fatigue" that can come from too many similar experiences.
                                </p>

                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    The safari can start from either Entebbe or Kigali, offering flexibility for international flights. Starting from Kigali actually shortens driving time to Bwindi, making the itinerary more comfortable. We include cultural experiences like the Batwa community visit, adding depth beyond wildlife viewing and supporting local communities directly.
                                </p>

                                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Best Time to Go</h3>
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    The dry seasons (June-September and December-February) offer the best trekking conditions with drier trails and clearer weather. However, Uganda's primates are active year-round, and the wet seasons (March-May, October-November) bring lush forests, fewer tourists, and often lower prices. Gorilla and chimpanzee sightings remain excellent throughout the year.
                                </p>
                            </div>
                        </section>

                        {/* Safari Highlights */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                                Safari Highlights
                            </h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-paw" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Gorilla Trekking in Bwindi</h3>
                                        <p className="text-gray-700">Life-changing hour with mountain gorillas in UNESCO World Heritage rainforest</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-tree" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Chimpanzee Tracking</h3>
                                        <p className="text-gray-700">Watch intelligent chimpanzees in Kibale's pristine forest canopy</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-lion" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Tree-Climbing Lions</h3>
                                        <p className="text-gray-700">Rare behavior found only in Ishasha and Tanzania's Lake Manyara</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-ship" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Kazinga Channel Cruise</h3>
                                        <p className="text-gray-700">Boat safari past massive hippo pods and diverse birdlife</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Batwa Cultural Experience</h3>
                                        <p className="text-gray-700">Learn ancient forest survival skills from indigenous Batwa people</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-binoculars" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Bigodi Wetland Walk</h3>
                                        <p className="text-gray-700">Community-run sanctuary with 138 bird species and 8 primate species</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Day-by-Day Itinerary */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                                Your 7-Day Itinerary
                            </h2>
                            <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                                <p className="text-gray-700 text-lg">
                                    <strong>📋 Tentative Itinerary:</strong> This itinerary is flexible and can be adjusted based on your travel preferences, budget, and specific interests. We customize every safari to match your unique needs!
                                </p>
                            </div>

                            <div className="space-y-6">
                                {itinerary.map((day) => (
                                    <div key={day.day} className="card">
                                        <div className="p-6">
                                            <div className="flex gap-4">
                                                <div className="flex-shrink-0">
                                                    <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                                                        {day.day}
                                                    </div>
                                                </div>
                                                <div className="flex-grow">
                                                    <h3 className="text-xl font-bold text-safari-green mb-2">{day.title}</h3>
                                                    <p className="text-gray-700 mb-4">{day.description}</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {day.highlights.map((highlight, index) => (
                                                            <span
                                                                key={index}
                                                                className="bg-safari-light text-safari-green px-3 py-1 rounded-full text-sm"
                                                            >
                                                                {highlight}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* What's Included/Excluded */}
                        <section className="mb-16">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="card p-6">
                                    <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                                        What's Included
                                    </h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">6 nights accommodation (full board)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">4x4 safari vehicle with expert driver-guide</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Gorilla trekking permit</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Chimpanzee tracking permit</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">All park entry fees</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Game drives and boat cruises</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Bigodi Wetland walk</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Bottled drinking water</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="card p-6">
                                    <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                                        <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                                        What's Not Included
                                    </h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">International flights</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Uganda/Rwanda visa</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Travel insurance</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Tips for guides and staff</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Personal expenses</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Alcoholic beverages</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Porter hire (optional but recommended)</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* FAQs */}
                        <FAQSection faqs={faqs} />

                        {/* Related Safari Experiences Carousel */}
                        <section>
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-4 text-center">
                                Explore More Safari Experiences
                            </h2>
                            <p className="text-center text-gray-700 mb-12 text-lg">
                                Discover other carefully curated safari adventures across Uganda
                            </p>

                            <SafariExperiencesCarousel
                                experiences={safariExperiences}
                                currentExperience="7-days-gorilla-chimp-safari"
                            />
                        </section>
                    </main>

                    {/* Sidebar */}
                    <aside className="lg:col-span-1">
                        <div className="sticky top-24">
                            <div className="card p-6 mb-8">
                                <h3 className="text-2xl font-bold text-safari-green mb-4">Safari Details</h3>
                                <div className="space-y-4 text-gray-700">
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Duration:</strong> 7 Days / 6 Nights</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Best Time:</strong> Year-round</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-map-marked-alt" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Parks:</strong> 3 National Parks</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Difficulty:</strong> Moderate</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Group:</strong> Private or small group</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-plane" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Start Point:</strong> Entebbe or Kigali</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                                >
                                    Get Custom Quote
                                </button>

                                <div className="bg-safari-green/5 border border-safari-green/20 rounded-lg p-4 mt-4">
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        <span className="font-bold text-safari-green">🌿 Travel with Purpose:</span> Part of your booking contributes to our <Link to="/planting-green-paths" className="text-safari-green font-semibold hover:underline">'Planting Green Paths'</Link> tree planting initiative, helping restore Uganda's ecosystems by securing a tree, planting and tree growth follow up. You can choose to participate in planting a tree activity during your safari or simply support the cause.
                                    </p>
                                </div>
                            </div>

                            <div className="card p-6 mb-8">
                                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                                    <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6 text-safari-gold" />
                                    Packing Essentials
                                </h3>
                                <ul className="space-y-2 text-sm text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Sturdy, waterproof hiking boots</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Long pants & sleeves (earth tones)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Waterproof rain jacket</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Gardening gloves for trekking</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Binoculars and camera (no flash)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Insect repellent, sunscreen, hat</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="card p-6">
                                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                                    <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                                    Health & Safety
                                </h3>
                                <ul className="space-y-3 text-gray-700 text-sm">
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span>Yellow fever vaccination required</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span>Malaria prophylaxis recommended</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span>Minimum age: 15 (gorillas), 12 (chimps)</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            <BookingModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                experience="7 Days Gorilla & Chimpanzee Trekking Safari"
            />
        </div>
    )
}
